import type { UpsellItem } from '@/lib/api/upsells';
import React, { createContext, useContext, useMemo } from 'react';
import { useProductsContextSafe } from './products-provider';
import { useUpsellsContextSafe } from './upsells-provider';

export type OrderBumpOfferItem = NonNullable<UpsellItem['orderBumpOffers']>[number];

interface OrderBumpsContextValue {
  orderBumps: OrderBumpOfferItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  variantMap: Map<string, { id: string; name: string; imageUrl: string | null }>;
  priceMap: Map<
    string,
    {
      id: string;
      currencyOptions: Record<string, { amount: number; date?: string; rate?: number; lock?: boolean }>;
      recurring: boolean;
      interval: string | null;
      intervalCount: number | null;
    }
  >;
}

const OrderBumpsContext = createContext<OrderBumpsContextValue | undefined>(undefined);

interface OrderBumpsProviderProps {
  children: React.ReactNode;
}

export const OrderBumpsProvider: React.FC<OrderBumpsProviderProps> = ({ children }) => {
  // Use upsells from the upsells provider
  const upsellsContext = useUpsellsContextSafe();
  // Use products from the products provider to get variant information
  const productsContext = useProductsContextSafe();

  if (!upsellsContext) {
    throw new Error('OrderBumpsProvider must be used within an UpsellsProvider');
  }

  const { upsells, loading, error, refetch } = upsellsContext;

  // Create a map of variantId -> variant info from products
  const variantMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; imageUrl: string | null }>();

    if (productsContext?.products) {
      productsContext.products.forEach((product) => {
        if (product.variants) {
          product.variants.forEach((variant) => {
            map.set(variant.id, {
              id: variant.id,
              name: variant.name,
              imageUrl: variant.imageUrl,
            });
          });
        }
      });
    }

    return map;
  }, [productsContext?.products]);

  // Create a map of priceId -> price info from products
  const priceMap = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        currencyOptions: Record<string, { amount: number; date?: string; rate?: number; lock?: boolean }>;
        recurring: boolean;
        interval: string | null;
        intervalCount: number | null;
      }
    >();

    if (productsContext?.products) {
      productsContext.products.forEach((product) => {
        if (product.variants) {
          product.variants.forEach((variant) => {
            if (variant.prices) {
              variant.prices.forEach((price) => {
                if (price.currencyOptions) {
                  map.set(price.id, {
                    id: price.id,
                    currencyOptions: price.currencyOptions,
                    recurring: price.recurring ?? false,
                    interval: price.interval ?? null,
                    intervalCount: price.intervalCount ?? null,
                  });
                }
              });
            }
          });
        }
      });
    }

    return map;
  }, [productsContext?.products]);

  // Extract order bump offers from upsells
  const orderBumps = useMemo(() => {
    return upsells
      .filter((upsell) => upsell.type === 'orderbump' && upsell.enabled && upsell.orderBumpOffers)
      .flatMap((upsell) =>
        upsell.orderBumpOffers!.map((offer) => ({
          ...offer,
          upsellId: upsell.id,
          upsell: {
            id: upsell.id,
            offerTitle: upsell.offerTitle,
            enabled: upsell.enabled ?? false,
          },
        })),
      );
  }, [upsells]);

  const value: OrderBumpsContextValue = {
    orderBumps,
    loading: loading || (productsContext?.loading ?? false),
    error: error || productsContext?.error || null,
    refetch,
    variantMap,
    priceMap,
  };

  return <OrderBumpsContext.Provider value={value}>{children}</OrderBumpsContext.Provider>;
};

export const useOrderBumpsContext = () => {
  const context = useContext(OrderBumpsContext);

  if (!context) {
    throw new Error('useOrderBumpsContext must be used within an OrderBumpsProvider');
  }

  return context;
};

/**
 * Safe version that returns null instead of throwing
 * Use this when the provider might not be available
 */
export const useOrderBumpsContextSafe = () => {
  const context = useContext(OrderBumpsContext);
  return context || null;
};
